import React, { Component } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { flowRight, omit } from 'lodash';
import { connect } from '../components/runtime-context';
import createHocName from '../services/create-hoc-name';
import { type Actions, type AppState } from '../types';
import preventClickInDemoMode, {
  type WithPreventClickInDemoMode,
} from './prevent-click-in-demo-mode';
import withAuth, { type WithAuth } from './with-auth';

const NAME = 'EnsureAuth';

/** Props this HOC supplies; they disappear from the wrapped component's public API. */
export type EnsureAuthInjected = WithAuth & {
  requestLogin: () => Promise<void>;
};

export type WithEnsureAuth = EnsureAuthInjected & {
  /** Passed through by the caller and wrapped, so it stays public. */
  onClick?: (event: React.MouseEvent) => void;
};

/**
 * Props left for the caller once `Injected` is supplied by the HOC.
 *
 * Some wrapped components (e.g. Button) come back from a lodash `flowRight`
 * HOC with their type erased, so `P` cannot be inferred. Fall back to a
 * pass-through in that case instead of narrowing everything to `{}`.
 */
type PublicProps<P, Injected> = unknown extends P
  ? Record<string, unknown>
  : Omit<P, keyof Injected>;

export default function ensureAuth<P>(WrappedComponent: React.ComponentType<P>) {
  const ChildComponent = preventClickInDemoMode(WrappedComponent);

  class EnsureAuthComponent extends Component<P & WithEnsureAuth> {
    static displayName = createHocName(NAME, WrappedComponent);

    handleClick = (event: React.MouseEvent) => {
      const { isAuthenticated, onClick, requestLogin } = this.props;
      if (isAuthenticated) {
        onClick && onClick(event);
      } else {
        event.preventDefault();
        event.stopPropagation();

        requestLogin().then(() => onClick && onClick(event));
      }
    };

    render() {
      const childProps = {
        ...omit(this.props, ['isAuthenticated', 'onClick', 'requestLogin']),
        onClick: this.handleClick,
      } as unknown as React.ComponentProps<typeof ChildComponent>;
      return <ChildComponent {...childProps} />;
    }
  }

  hoistNonReactStatics(EnsureAuthComponent, ChildComponent);

  const mapRuntimeProps = (_state: AppState, _ownProps: {}, actions: Actions) => ({
    requestLogin: actions.requestLoginPromisified,
  });

  // Same Matching<> limitation as preventClickInDemoMode: this HOC supplies
  // everything in WithEnsureAuth and passes the remaining props through.
  return flowRight(connect(mapRuntimeProps), withAuth)(EnsureAuthComponent) as React.ComponentType<
    PublicProps<P, EnsureAuthInjected>
  >;
}
